import { useMemo, useState } from "react"
import { Check, ChevronsUpDown, Globe } from "lucide-react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command"
import {
  canonicalTimezone,
  entryForTimezone,
  findTimezone,
  formatTimezoneLabel,
  getAllTimezones,
  getDefaultTimezone,
  type TimezoneEntry,
} from "@/shared/utils"
import { cn } from "../lib/utils"

/**
 * Searchable timezone picker backed by a curated list of major cities, one
 * per offset in common use plus the well-known business hubs, with current
 * DST-aware offsets.
 *
 * Drop-in replacement for the various static `<Select>` lists scattered
 * across the meeting + webinar flows: props match the controlled-select
 * shape we already use (`value`, `onChange`).
 */
interface TimezoneSelectProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  id?: string
  disabled?: boolean
  /** Apply to the trigger button so callers can size/space it. */
  className?: string
  /** Side of the trigger to render the popover. */
  align?: "start" | "center" | "end"
  /** Compact (no leading globe icon). */
  hideIcon?: boolean
  /**
   * Variant — "default" matches the rest of the schedule form, "compact"
   * is the borderless inline trigger used inside TimeZonePlannerDialog
   * cards.
   */
  variant?: "default" | "compact"
}

export function TimezoneSelect({
  value,
  onChange,
  placeholder = "Select a timezone",
  id,
  disabled = false,
  className,
  align = "start",
  hideIcon = false,
  variant = "default",
}: TimezoneSelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")

  // The curated list is deliberately short, so it can miss a zone a meeting
  // was actually saved with (an older record, or a viewer whose own zone
  // isn't one of the curated cities). Both get folded in here rather than
  // left to disappear from the dropdown while still being the active value.
  const timezones = useMemo(() => {
    const base = getAllTimezones()
    const extras: TimezoneEntry[] = []
    const seen = new Set(base.map((t) => t.id))

    const include = (id: string | undefined) => {
      if (!id) return
      const canonical = canonicalTimezone(id)
      if (seen.has(canonical)) return
      const entry = entryForTimezone(canonical)
      if (!entry) return
      seen.add(canonical)
      extras.push(entry)
    }

    include(value)
    include(getDefaultTimezone())

    return extras.length > 0 ? [...base, ...extras] : base
  }, [value])

  // Resolve the active entry; if a legacy/unknown value is passed, render the
  // raw id so we don't silently lose the selection.
  const selected = useMemo(() => {
    if (!value) return undefined
    return findTimezone(value) ?? null
  }, [value])

  const selectedLabel = !value
    ? placeholder
    : selected
      ? formatTimezoneLabel(selected.id)
      : value

  const handleSelect = (id: string) => {
    onChange(id)
    setOpen(false)
    setQuery("")
  }

  const isCompact = variant === "compact"

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn(
            "flex w-full items-center justify-between gap-2 text-sm",
            isCompact
              ? "h-8 px-1 bg-transparent text-left"
              : "h-10 px-3 rounded-md border border-input bg-card hover:bg-accent/30 transition-colors text-left",
            disabled && "opacity-50 cursor-not-allowed",
            className,
          )}
        >
          <span className="flex items-center gap-2 min-w-0">
            {!hideIcon && !isCompact && (
              <Globe className="w-4 h-4 shrink-0 text-muted-foreground" />
            )}
            <span
              className={cn(
                "truncate",
                value ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {selectedLabel}
            </span>
          </span>
          <ChevronsUpDown className="w-4 h-4 shrink-0 text-muted-foreground opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="w-[min(420px,calc(100vw-2rem))] p-0"
      >
        <Command
          // `value`/`keywords` on each item drive cmdk's fuzzy search: by
          // joining city + region + IANA id + offset we let users search
          // "ny", "new york", "America/New_York", or "GMT-5" interchangeably.
          shouldFilter={true}
        >
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Search city, region, or offset…"
          />
          <CommandList>
            <CommandEmpty>No timezone matches.</CommandEmpty>
            <CommandGroup>
              {timezones.map((tz) => {
                const searchKey = `${tz.city} ${tz.region} ${tz.id} ${tz.offsetLabel} GMT`
                return (
                  <CommandItem
                    key={tz.id}
                    value={searchKey}
                    onSelect={() => handleSelect(tz.id)}
                  >
                    <span className="font-mono text-xs text-muted-foreground w-20 shrink-0">
                      ({tz.offsetLabel})
                    </span>
                    <span className="flex-1 truncate">
                      <span className="text-foreground">{tz.city}</span>
                      <span className="text-muted-foreground">
                        {tz.region !== "-" ? ` - ${tz.region}` : ""}
                      </span>
                    </span>
                    <Check
                      className={cn(
                        "w-4 h-4 shrink-0",
                        value === tz.id ? "opacity-100 text-primary" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export { getDefaultTimezone, getAllTimezones, formatTimezoneLabel }
